/* ============================================================
   OTTR — the site's own behaviour, ported from the walkthrough
   prototype (artifact N8TGePFw43f75mhhK1K2aC).

   What changed on the way in, and nothing else did:
   · the prototype was one HTML file with fourteen <main class="page">
     blocks and a switcher. On Shopify every page is a real URL, so the
     switcher, the [data-go] click handler and the footer <template>
     are gone and the links are ordinary hrefs;
   · breadcrumbs are rendered by Liquid rather than rebuilt in JS;
   · every `.page.on` scope became the document;
   · the prototype wrote data-rev on each full-screen screen by hand;
     here it is one line, so a page is just its markup;
   · it runs in passes rather than once. Shopify's theme editor re-renders
     a section on every keystroke, and anything arrived-since would have
     been left unobserved — invisible copy that reads as a broken page.
     Every pass below is idempotent, and inside the editor a section is
     revealed the moment it is tagged rather than waiting to be scrolled
     into, because you are editing it, not reading it.
   · scroll listeners are attached with `capture:true`. Shopify's own
     theme-preview chrome (the admin bar) wraps the page in a scrollable
     `.page-wrapper` div rather than letting `window` itself scroll —
     a bubble-phase `window.addEventListener('scroll', ...)` never fires
     in that wrapper, so the section rail and the growing card looked
     frozen in preview even though they work on the published site.
     Capture-phase listening on window catches a scroll fired on any
     descendant, so both contexts behave the same.
   · 17 Sep: the scroller is resolved at runtime. It is NOT only the
     preview that scrolls `.page-wrapper` — base.css makes it the scroll
     container at every width from 990px up, on the published site too
     (see claude/responsive-architecture.md §1). Anything that scrolls
     the page by `window.scrollTo` / reads `window.scrollY` was a no-op
     on a desktop, so the rail links and the FAQ rail went nowhere there.
     `scrollPageTo` / `pageScrollY` below pick the right box.
   ============================================================ */

(function(){
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var editing = !!(window.Shopify && window.Shopify.designMode);
  var io, revIO;

  /* ── 0 · which box scrolls ─────────────────────────────
     ≥990px: .page-wrapper is a 100dvh box with overflow-y:auto and the
     document does not scroll. <990px: the document scrolls. Decide from
     what is actually scrollable right now, never from the width. */
  function scroller(){
    var pw = document.querySelector('.page-wrapper');
    return (pw && pw.scrollHeight > pw.clientHeight + 2) ? pw : null;
  }
  function pageScrollY(){
    var pw = scroller();
    return pw ? pw.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);
  }
  function scrollPageTo(y){
    var pw = scroller();
    var opts = {top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth'};
    if (pw) pw.scrollTo(opts); else window.scrollTo(opts);
  }

  /* ── 1 · the menu ──────────────────────────────── */
  var drop, menuBtns = [], secBtns = [], barwrapEl, wordmarkEl, navEl;
  var hoverTimer, leaveTimer;

  function lit(sec){
    document.querySelectorAll('.drop-col').forEach(function(c){
      c.classList.toggle('lit', !!sec && c.dataset.col === sec);
    });
  }
  function setMenu(open){
    if (!drop) return;
    drop.classList.toggle('open', open);
    document.body.classList.toggle('menu-open', open);
    menuBtns.forEach(function(b){ b.setAttribute('aria-expanded', String(open)); });
    spreadLabels(open);
    if (!open) lit(null);
  }
  function closeMenu(){ clearTimeout(hoverTimer); clearTimeout(leaveTimer); setMenu(false); }
  function openMenu(){ clearTimeout(leaveTimer); setMenu(true); }

  function setNavLeft(){
    if (!barwrapEl || !wordmarkEl) return;
    var left = Math.round(wordmarkEl.offsetLeft + wordmarkEl.offsetWidth) + 34;
    barwrapEl.style.setProperty('--nav-left', left + 'px');
  }
  function clearSpread(){ secBtns.forEach(function(b){ b.style.transform = ''; }); }

  function spreadLabels(on){
    if (!on || window.innerWidth <= 1040 || reduce){ clearSpread(); return; }
    // offsetLeft is layout-based, so it is unaffected by the transforms we apply —
    // measuring it means we never have to reset and re-measure mid-animation.
    var iconsEl = document.querySelector('.icons');
    var leftLimit = wordmarkEl ? wordmarkEl.offsetLeft + wordmarkEl.offsetWidth + 20 : null;
    var rightLimit = iconsEl ? iconsEl.offsetLeft - 20 : null;
    secBtns.forEach(function(b){
      var link = document.querySelector('.drop-col[data-col="' + b.dataset.sec + '"] a');
      if (!link) return;
      // The bar's nav is centred with translate(-50%), so its own offset needs that
      // half-width taken off before the buttons inside it mean anything.
      var navX = navEl ? navEl.offsetLeft - navEl.offsetWidth / 2 : 0;
      var btnX = navX + b.offsetLeft;
      var target = link.offsetLeft + 1;   // +1 = the drop's border
      if (leftLimit !== null) target = Math.max(target, leftLimit);
      if (rightLimit !== null) target = Math.min(target, rightLimit - b.offsetWidth);
      b.style.transform = 'translateX(' + Math.round(target - btnX) + 'px)';
    });
  }

  function wireHead(){
    drop = document.getElementById('mainmenu');
    barwrapEl = document.querySelector('.barwrap');
    wordmarkEl = document.querySelector('.wordmark');
    navEl = document.querySelector('.nav');
    menuBtns = Array.prototype.slice.call(document.querySelectorAll('[data-menu]'));
    secBtns = Array.prototype.slice.call(document.querySelectorAll('.nav [data-sec]'));

    menuBtns.forEach(function(b){
      if (b.dataset.ottrWired) return;
      b.dataset.ottrWired = '1';
      b.addEventListener('click', function(e){
        e.stopPropagation();
        if (drop && drop.classList.contains('open')) closeMenu(); else openMenu();
      });
      // Pointer users get it on hover, but only with intent.
      if (window.matchMedia('(hover:hover) and (pointer:fine)').matches){
        b.addEventListener('mouseenter', function(){
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(openMenu, 110);
        });
        b.addEventListener('mouseleave', function(){ clearTimeout(hoverTimer); });
      }
    });

    // Hovering or focusing a label lights the column it belongs to.
    secBtns.forEach(function(b){
      if (b.dataset.ottrLit) return;
      b.dataset.ottrLit = '1';
      ['mouseenter','focus'].forEach(function(ev){
        b.addEventListener(ev, function(){ lit(b.dataset.sec); });
      });
      ['mouseleave','blur'].forEach(function(ev){
        b.addEventListener(ev, function(){ lit(null); });
      });
    });

    var hdrEl = document.querySelector('.hdr');
    if (hdrEl && !hdrEl.dataset.ottrWired && window.matchMedia('(hover:hover) and (pointer:fine)').matches){
      hdrEl.dataset.ottrWired = '1';
      hdrEl.addEventListener('mouseleave', function(){
        leaveTimer = setTimeout(function(){ closeMenu(); lit(null); }, 240);
      });
      hdrEl.addEventListener('mouseenter', function(){ clearTimeout(leaveTimer); });
    }

    setNavLeft();
  }

  window.addEventListener('resize', function(){
    setNavLeft();
    if (window.innerWidth <= 1040) clearSpread();
    else if (document.body.classList.contains('menu-open')) spreadLabels(true);
  });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(setNavLeft);

  document.addEventListener('keydown', function(e){
    if (e.key === 'Escape' && drop && drop.classList.contains('open')){
      closeMenu();
      var first = document.querySelector('.nav [data-menu]');
      if (first) first.focus();
    }
  });
  document.addEventListener('click', function(e){
    if (drop && drop.classList.contains('open') && !e.target.closest('.hdr')) closeMenu();
  });

  /* ── 2 · the section rail ────────────────────────────────
     Replaces the header once you are past the hero, tracks where you are,
     and keeps Buy on screen the whole way down. */
  function wireRail(){
    var subnav = document.querySelector('.subnav');
    var sentinel = document.querySelector('.navsentinel');
    var links = Array.prototype.slice.call(document.querySelectorAll('.subnav-links a'));
    var dots = Array.prototype.slice.call(document.querySelectorAll('.subnav-dots i'));

    // Taken = the rail has gone past the TOP of the viewport, and only that.
    //
    // This used to be an IntersectionObserver on the sentinel with
    //   taken = !isIntersecting
    // which is also true BELOW the fold — on a phone the hero and gallery run
    // ~2,900px before the rail, so on load the header was already gone. And
    // an observer only fires on a crossing: a fast fling, an instant scroll
    // or a restored scroll position can carry a 1px sentinel from above the
    // viewport to below it without it ever being inside, so no callback,
    // and the class went stale in whichever state it was left. That is how
    // the header stayed hidden after scrolling back to the top.
    //
    // So it is read off the sentinel's rect on every scroll frame instead
    // (rAF-throttled, same tick that marks the rail) and once on wire. No
    // crossing to miss.
    var syncTaken = function(){
      var s = document.querySelector('.navsentinel');
      if (!s) return;
      var taken = s.getBoundingClientRect().top < 0;
      if (taken && !document.body.classList.contains('nav-taken')) closeMenu();
      document.body.classList.toggle('nav-taken', taken);
    };
    if (sentinel && !sentinel.dataset.ottrWired){
      sentinel.dataset.ottrWired = '1';
      syncTaken();
    }

    links.forEach(function(a){
      if (a.dataset.ottrWired) return;
      a.dataset.ottrWired = '1';
      a.addEventListener('click', function(e){
        var el = document.querySelector(a.getAttribute('href'));
        if (!el) return;
        e.preventDefault();
        var off = subnav ? subnav.offsetHeight + 10 : 90;
        scrollPageTo(el.getBoundingClientRect().top + pageScrollY() - off);
      });
    });

    if (!links.length || window.__ottrRail) return;
    window.__ottrRail = true;
    var queued = false;
    var mark = function(){
      queued = false;
      syncTaken();
      var bar = document.querySelector('.subnav');
      var line = (bar ? bar.offsetHeight : 80) + 40, best = 0;
      var ls = Array.prototype.slice.call(document.querySelectorAll('.subnav-links a'));
      var ds = Array.prototype.slice.call(document.querySelectorAll('.subnav-dots i'));
      ls.forEach(function(a, i){
        var el = document.querySelector(a.getAttribute('href'));
        if (el && el.getBoundingClientRect().top <= line) best = i;
      });
      ls.forEach(function(a, i){ a.classList.toggle('on', i === best); });
      ds.forEach(function(d, i){ d.classList.toggle('on', i === best); });
    };
    window.addEventListener('scroll', function(){
      if (queued) return;
      queued = true;
      requestAnimationFrame(mark);
    }, {passive:true, capture:true});
    mark();
  }

  /* ── 3 · Sweep's motion words ─────────────────────────
     The upright layer wipes off left to right, leaving the italic. */
  function wireWords(){
    var words = document.querySelectorAll('.sw:not(.swept)');
    if (!words.length) return;
    if (reduce || editing || !('IntersectionObserver' in window)){
      words.forEach(function(el){ el.classList.add('swept'); });
      return;
    }
    if (!io){
      io = new IntersectionObserver(function(entries){
        entries.forEach(function(en){
          if (!en.isIntersecting) return;
          var el = en.target;
          setTimeout(function(){ el.classList.add('swept'); }, 160);
          io.unobserve(el);
        });
      }, {threshold:.6, rootMargin:'0px 0px -8% 0px'});
    }
    words.forEach(function(el){ io.observe(el); });
    // Nothing is left mid-state: anything still upright after 6s sweeps anyway.
    setTimeout(function(){
      document.querySelectorAll('.sw:not(.swept)').forEach(function(el){ el.classList.add('swept'); });
    }, 6000);
  }

  /* ── 4 · reveals ─────────────────────────
     Nothing is hidden until this runs, so a blocked script means a visible
     page rather than a blank one. Every section becomes a reveal group:
     rather than tagging a hundred elements by hand, take the content
     block's own children — and where a child is a grid or a list, stagger
     its items instead of the container, so rows arrive one at a time. */
  var GROUPS = /\b(bento|wall|shelf|rows|ladder|terms|denoms|statgrid|stat3|scroller|gal|faqwrap|split|pdp|specgrid|trust|drop-cols|drop-bento|citems)\b/;

  function wireReveals(){
    document.querySelectorAll('.sec, .banner').forEach(function(sec){
      if (sec.hasAttribute('data-rev')) return;
      if (sec.closest('.grow-sec') || sec.classList.contains('fs')) return;
      sec.setAttribute('data-rev', '');
      var host = sec.querySelector('.in, .in--nar') || sec;
      var kids = Array.prototype.slice.call(host.children);
      // A wrapper with a single child is not a rhythm — go one level in.
      if (kids.length === 1 && kids[0].children.length > 1){
        host = kids[0];
        kids = Array.prototype.slice.call(host.children);
      }
      var i = 0;
      kids.forEach(function(k){
        if (GROUPS.test(k.className) && k.children.length > 1){
          Array.prototype.slice.call(k.children).forEach(function(c){
            c.classList.add('rvitem');
            c.style.setProperty('--i', i++);
          });
        } else {
          k.classList.add('rvitem');
          k.style.setProperty('--i', i++);
        }
      });
    });

    // A full-screen screen is one beat, not a stagger: it is its own reveal
    // group and nothing inside it gets an --i from the pass above.
    document.querySelectorAll('.fs:not([data-rev])').forEach(function(s){ s.setAttribute('data-rev',''); });

    var revs = Array.prototype.slice.call(document.querySelectorAll('[data-rev]:not(.rv)'));
    if (!revs.length) return;

    revs.forEach(function(s){
      Array.prototype.slice.call(s.querySelectorAll('.rvitem, .fsgrid b, .fstype i')).forEach(function(el, i){
        if (!el.style.getPropertyValue('--i')) el.style.setProperty('--i', i);
      });
    });

    // In the editor you are looking at the section you just changed, not
    // scrolling toward it. Show it at once rather than leaving it at zero.
    if (reduce || editing || !('IntersectionObserver' in window)){
      revs.forEach(function(s){ s.classList.add('rv'); });
      return;
    }

    document.body.classList.add('armed');
    if (!revIO){
      revIO = new IntersectionObserver(function(entries){
        entries.forEach(function(en){
          if (!en.isIntersecting) return;
          en.target.classList.add('rv');
          revIO.unobserve(en.target);
        });
      }, {threshold:.18, rootMargin:'0px 0px -6% 0px'});
    }
    revs.forEach(function(s){ revIO.observe(s); });
    // Deadline: nothing stays armed forever.
    setTimeout(function(){
      document.querySelectorAll('[data-rev]').forEach(function(s){ s.classList.add('rv'); });
    }, 6000);
  }

  /* ── 5 · the card that opens out of its smallest state ────────────────
     Every `.grow-sec` on the page gets its own tracked position — a page
     can have more than one of these cards (hero, mechanism, the flip),
     and each one opens on its own scroll math, not just the first found. */
  var growTracked = [];
  function wireGrow(){
    if (reduce) return;
    var secs = Array.prototype.slice.call(document.querySelectorAll('.grow-sec'));
    var newOnes = secs.filter(function(s){ return !s.dataset.ottrWired; });
    if (!newOnes.length) return;
    newOnes.forEach(function(s){ s.dataset.ottrWired = '1'; growTracked.push(s); });

    var queued = false;
    var tick = function(){
      queued = false;
      growTracked.forEach(function(growSec){
        var span = growSec.offsetHeight - window.innerHeight;
        if (span <= 0) { growSec.style.setProperty('--p','1'); return; }
        var p = -growSec.getBoundingClientRect().top / span;
        p = Math.min(1, Math.max(0, p / 0.78));   // full size before the section releases
        growSec.style.setProperty('--p', p.toFixed(3));
      });
    };
    var onScroll = function(){
      if (queued) return;
      queued = true;
      requestAnimationFrame(tick);
    };
    if (!window.__ottrGrowWired){
      window.__ottrGrowWired = true;
      window.addEventListener('scroll', onScroll, {passive:true, capture:true});
      window.addEventListener('resize', onScroll);
    }
    tick();
  }

  /* ── 6 · single-choice groups and the FAQ rail ────────────────── */
  function wirePickers(){
    [['.varpick','.var'],['.denoms','.denom']].forEach(function(pair){
      document.querySelectorAll(pair[0]).forEach(function(g){
        if (g.dataset.ottrWired) return;
        g.dataset.ottrWired = '1';
        g.addEventListener('click', function(e){
          var b = e.target.closest(pair[1]);
          if (!b) return;
          g.querySelectorAll(pair[1]).forEach(function(x){ x.setAttribute('aria-pressed','false'); });
          b.setAttribute('aria-pressed','true');
        });
      });
    });

    var rail = Array.prototype.slice.call(document.querySelectorAll('.qa-rail a'));
    rail.forEach(function(a){
      if (a.dataset.ottrWired) return;
      a.dataset.ottrWired = '1';
      a.addEventListener('click', function(e){
        var el = document.querySelector(a.getAttribute('href'));
        if (!el) return;
        e.preventDefault();
        rail.forEach(function(x){ x.classList.toggle('on', x === a); });
        scrollPageTo(el.getBoundingClientRect().top + pageScrollY() - 96);
      });
    });
    if (rail.length && !window.__ottrQa){
      window.__ottrQa = true;
      window.addEventListener('scroll', function(){
        var rs = Array.prototype.slice.call(document.querySelectorAll('.qa-rail a'));
        var best = 0;
        rs.forEach(function(a, i){
          var el = document.querySelector(a.getAttribute('href'));
          if (el && el.getBoundingClientRect().top <= 140) best = i;
        });
        rs.forEach(function(a, i){ a.classList.toggle('on', i === best); });
      }, {passive:true, capture:true});
    }
  }

  /* ── 7 · the circle ────────────────────────
     Only where there is a real pointer. The native cursor is hidden only
     once this has run, so a blocked script leaves a working arrow rather
     than none. Off in the editor, where you need the real one. */
  function startCursor(){
    if (reduce || editing) return;
    if (!window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    if (document.querySelector('.cursor-ring')) return;

    var ring = document.createElement('div');
    ring.className = 'cursor-ring';
    ring.setAttribute('aria-hidden','true');
    document.body.appendChild(ring);

    var mx = 0, my = 0, rqueued = false;
    function frame(){
      rqueued = false;
      ring.style.transform = 'translate(' + mx + 'px,' + my + 'px)';
    }

    document.addEventListener('mousemove', function(e){
      mx = e.clientX; my = e.clientY;
      if (!document.body.classList.contains('cursoring')) document.body.classList.add('cursoring');
      if (!rqueued){ rqueued = true; requestAnimationFrame(frame); }
      var hot = e.target.closest('a,button,summary,.dcard,.card,.prod,.denom,.var,.media,.vid');
      document.body.classList.toggle('cursor-hot', !!hot);
    }, {passive:true});

    document.addEventListener('mousedown', function(){ document.body.classList.add('cursor-down'); });
    document.addEventListener('mouseup',   function(){ document.body.classList.remove('cursor-down'); });
    document.addEventListener('mouseleave',function(){ document.body.classList.remove('cursoring'); });
    document.addEventListener('mouseenter',function(){ document.body.classList.add('cursoring'); });
  }

  /* ── boot ──────────────────────── */
  function pass(){
    wireHead();
    wireRail();
    wireWords();
    wireReveals();
    wireGrow();
    wirePickers();
  }

  function boot(){
    pass();
    startCursor();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Tinker hydrates sections after first paint and the editor re-runs them,
  // so re-arm on both rather than assuming one pass.
  document.addEventListener('shopify:section:load', pass);
  document.addEventListener('shopify:section:reorder', pass);
  document.addEventListener('shopify:block:select', pass);
  setTimeout(pass, 700);
  setTimeout(pass, 2000);
})();
